import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@et/environment';
import { catchError, forkJoin, map, Observable, of, retry } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FileCompressionService {
  private http = inject(HttpClient);

  /**
   * Compresses a single PDF file using the backend compression API.
   *
   * This method sends the provided file to the server for compression and returns
   * an observable that emits the compressed file as a new File object.
   * If compression fails, it returns the original file.
   *
   * @param {File} file - The PDF file to compress.
   * @returns {Observable<File>} An observable that emits the compressed File, or the original file if compression fails.
   */
  compress(file: File): Observable<File> {
    const formData = new FormData();
    formData.append('pdf', file);
    return this.http
      .post(
        environment.packetService.domain + '/api/packet/file/PdfCompression',
        formData,
        { responseType: 'blob' },
      )
      .pipe(
        retry({ count: 3, delay: 100 }),
        map((blob) => this.makeFileFromResponse(blob, file)),
        catchError((error) => {
          console.log('File compression failed:', error?.error);
          return of(file); // Return the original file in case of error
        }),
      );
  }

  /**
   * Compresses multiple PDF files in parallel.
   *
   * This method compresses each file in the provided array using the `compress` method,
   * and returns an observable that emits an array of compressed files.
   *
   * @param {File[]} files - The array of PDF files to compress.
   * @returns {Observable<File[]>} An observable that emits an array of compressed files.
   */
  compressFiles(files: File[]): Observable<File[]> {
    return forkJoin(files.map((file) => this.compress(file)));
  }

  /**
   * Creates a new File object from the server response blob, preserving the
   * original file's name,type, and last modified date.
   *
   * @param {Blob} blob - The blob returned from the server after compression.
   * @param {File} file - The original file to copy metadata from.
   * @returns {File} The new File object created from the blob.
   */
  private makeFileFromResponse(blob: Blob, file: File): File {
    return new File([blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }
}
